/*
Copyright 2025 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { logger } from "matrix-js-sdk/lib/logger";
import {
  EventType,
  KnownMembership,
  type IEvent,
  type MatrixClient,
  MatrixEvent,
  type Room,
} from "matrix-js-sdk";

import { callTraceJournal, describeTraceError } from "../telemetry";
import { ElementWidgetActions, widget } from "../widget";
import { throwIfAborted } from "../recovery/abort";

const HYDRATION_COOLDOWN_MS = 5000;

export type RoomMembersHydrationReason =
  | "rtc_member_missing"
  | "room_member_missing"
  | "manual_reconcile";
export type RoomMembersHydrationSource =
  | "member_displaynames"
  | "media_items"
  | "manual_reconcile";

export interface RoomMembersHydrationOptions {
  forceServer: boolean;
  reason: RoomMembersHydrationReason;
  source: RoomMembersHydrationSource;
  userId?: string;
}

export interface RoomMembersHydrator {
  ensureRoomMembersHydrated(
    roomId: string,
    options: RoomMembersHydrationOptions,
  ): Promise<boolean>;
}

export interface AuthoritativeRoomMembersHydrator {
  hydrateAllRoomMembers(roomId: string, signal: AbortSignal): Promise<void>;
}

export type RoomMembersHydrationAdapter = RoomMembersHydrator &
  AuthoritativeRoomMembersHydrator;

/** Результат единственной физической загрузки списка участников комнаты. */
interface RoomMembersHydrationRunResult {
  requestSucceeded: boolean;
}

abstract class BaseRoomMembersHydrator implements RoomMembersHydrator {
  private activeHydration?: Promise<RoomMembersHydrationRunResult>;
  private readonly lastAttemptByRoomId = new Map<string, number>();
  private applicationGeneration = 0;

  public constructor(
    protected readonly client: MatrixClient,
    protected readonly room: Room,
  ) {}

  public async ensureRoomMembersHydrated(
    roomId: string,
    options: RoomMembersHydrationOptions,
  ): Promise<boolean> {
    const tracePayload = this.getTracePayload(options);

    if (roomId !== this.room.roomId) {
      this.recordSkipped(tracePayload, "foreign_room");
      logger.warn("[RoomMembersHydrator] Получен запрос для чужой комнаты", {
        expectedRoomId: this.room.roomId,
        roomId,
      });
      return false;
    }

    if (options.userId && this.room.getMember(options.userId)) {
      this.recordSkipped(tracePayload, "already_present");
      return true;
    }

    logger.info("[RoomMembersHydrator] hydration requested", {
      forceServer: options.forceServer,
      mode: this.getMode(),
      reason: options.reason,
      roomId,
      userId: options.userId ?? null,
    });

    if (this.activeHydration) {
      this.recordSkipped(tracePayload, "active_hydration");
      const runResult = await this.activeHydration;
      return this.isResolved(options, runResult);
    }

    if (this.isCoolingDown(roomId)) {
      this.recordSkipped(tracePayload, "cooldown");
      logger.info("[RoomMembersHydrator] hydration skipped due to cooldown", {
        mode: this.getMode(),
        roomId,
        userId: options.userId ?? null,
      });
      return this.isResolved(options);
    }

    this.lastAttemptByRoomId.set(roomId, Date.now());

    const generation = this.applicationGeneration;
    const hydration = this.runHydration(roomId, options, generation).finally(
      () => {
        // Завершение старой операции не должно очищать ссылку на более новую.
        if (this.activeHydration === hydration) {
          this.activeHydration = undefined;
        }
      },
    );
    this.activeHydration = hydration;

    const runResult = await hydration;
    return this.isResolved(options, runResult);
  }

  private async runHydration(
    roomId: string,
    options: RoomMembersHydrationOptions,
    generation: number,
  ): Promise<RoomMembersHydrationRunResult> {
    const tracePayload = this.getTracePayload(options);
    const startedAt = performance.now();
    const startedEventId = callTraceJournal.record({
      eventType: "matrix.room_members_recovery_started",
      stage: "matrix",
      outcome: "started",
      payload: tracePayload,
    });

    logger.info("[RoomMembersHydrator] hydration started", {
      forceServer: options.forceServer,
      mode: this.getMode(),
      reason: options.reason,
      roomId,
      userId: options.userId ?? null,
    });

    try {
      await this.hydrateRoomMembers(roomId, options, generation);
      const resolved = options.userId
        ? !!this.room.getMember(options.userId)
        : true;
      callTraceJournal.record({
        eventType: "matrix.room_members_recovery_succeeded",
        stage: "matrix",
        severity: resolved ? "info" : "warn",
        outcome: "succeeded",
        reasonCode: resolved ? undefined : "member_still_missing",
        relatedEventId: startedEventId,
        durationMs: Math.round(performance.now() - startedAt),
        payload: {
          ...tracePayload,
          resolved,
        },
      });
      logger.info("[RoomMembersHydrator] hydration succeeded", {
        forceServer: options.forceServer,
        mode: this.getMode(),
        reason: options.reason,
        resolved,
        roomId,
        userId: options.userId ?? null,
      });
      return { requestSucceeded: true };
    } catch (error) {
      callTraceJournal.record({
        eventType: "matrix.room_members_recovery_failed",
        stage: "matrix",
        severity: "error",
        outcome: "failed",
        reasonCode: "room_members_recovery_failed",
        relatedEventId: startedEventId,
        durationMs: Math.round(performance.now() - startedAt),
        payload: {
          ...tracePayload,
          error: describeTraceError(error),
        },
      });
      logger.error("[RoomMembersHydrator] hydration failed", {
        forceServer: options.forceServer,
        mode: this.getMode(),
        reason: options.reason,
        roomId,
        userId: options.userId ?? null,
      });
      logger.error(error);
      return { requestSucceeded: false };
    }
  }

  /** Проверяет условие успеха конкретного вызова после общей гидрации. */
  private isResolved(
    options: RoomMembersHydrationOptions,
    runResult?: RoomMembersHydrationRunResult,
  ): boolean {
    if (options.userId) {
      return this.room.getMember(options.userId) !== null;
    }

    return runResult?.requestSucceeded ?? false;
  }

  private isCoolingDown(roomId: string): boolean {
    const lastAttemptTs = this.lastAttemptByRoomId.get(roomId);
    return (
      typeof lastAttemptTs === "number" &&
      Date.now() - lastAttemptTs < HYDRATION_COOLDOWN_MS
    );
  }

  private getTracePayload(options: RoomMembersHydrationOptions): {
    mode: "standalone" | "widget";
    reason: RoomMembersHydrationReason;
    forceServer: boolean;
    source: RoomMembersHydrationSource;
    userId: string | null;
  } {
    return {
      mode: this.getMode(),
      reason: options.reason,
      forceServer: options.forceServer,
      source: options.source,
      userId: options.userId ?? null,
    };
  }

  private recordSkipped(
    tracePayload: {
      mode: "standalone" | "widget";
      reason: RoomMembersHydrationReason;
      forceServer: boolean;
      source: RoomMembersHydrationSource;
      userId: string | null;
    },
    skipReason:
      | "cooldown"
      | "already_present"
      | "foreign_room"
      | "active_hydration",
  ): void {
    callTraceJournal.record({
      eventType: "matrix.room_members_recovery_skipped",
      stage: "matrix",
      outcome: "cancelled",
      reasonCode: skipReason,
      payload: {
        ...tracePayload,
        skipReason,
      },
    });
  }

  protected abstract hydrateRoomMembers(
    roomId: string,
    options: RoomMembersHydrationOptions,
    generation: number,
  ): Promise<void>;

  protected abstract getMode(): "standalone" | "widget";

  /**
   * Новый authoritative pass инвалидирует применение результатов всех
   * запросов, начатых раньше на этом же экземпляре hydrator.
   */
  protected beginAuthoritativeHydration(): number {
    this.applicationGeneration += 1;
    return this.applicationGeneration;
  }

  protected canApplyHydration(generation: number): boolean {
    return generation === this.applicationGeneration;
  }
}

async function fetchMembersFromServer(
  client: MatrixClient,
  roomId: string,
): Promise<MatrixEvent[]> {
  const response = await client.members(
    roomId,
    undefined,
    KnownMembership.Leave,
  );
  return response.chunk.map(client.getEventMapper()) as MatrixEvent[];
}

function applyRoomMemberEvents(room: Room, events: MatrixEvent[]): void {
  prepareRoomStateForHydration(room);
  room.currentState.markOutOfBandMembersStarted();
  room.currentState.setOutOfBandMembers(events);
}

async function fetchMembersFromWidgetHost(
  roomId: string,
): Promise<MatrixEvent[]> {
  if (!widget) {
    throw new Error("Widget mode hydration requested without widget API");
  }
  return (
    await widget.api.readStateEvents(
      EventType.RoomMember,
      undefined,
      undefined,
      [roomId],
    )
  ).map((event) => new MatrixEvent(event as Partial<IEvent>));
}

function prepareRoomStateForHydration(room: Room): void {
  if (room.currentState.outOfBandMembersReady()) {
    room.currentState.clearOutOfBandMembers();
  }
}

export class StandaloneRoomMembersHydrator
  extends BaseRoomMembersHydrator
  implements RoomMembersHydrator, AuthoritativeRoomMembersHydrator
{
  public async hydrateAllRoomMembers(
    roomId: string,
    signal: AbortSignal,
  ): Promise<void> {
    if (roomId !== this.room.roomId) {
      throw new Error("Authoritative hydration requested for foreign room");
    }
    const generation = this.beginAuthoritativeHydration();
    throwIfAborted(signal);
    const events = await fetchMembersFromServer(this.client, roomId);
    // Matrix SDK не принимает AbortSignal: до этой проверки нельзя менять
    // локальный RoomState результатом уже отменённой epoch.
    throwIfAborted(signal);
    if (!this.canApplyHydration(generation)) return;
    applyRoomMemberEvents(this.room, events);
  }

  protected override async hydrateRoomMembers(
    roomId: string,
    options: RoomMembersHydrationOptions,
    generation: number,
  ): Promise<void> {
    if (options.forceServer) {
      const events = await fetchMembersFromServer(this.client, roomId);
      if (this.canApplyHydration(generation)) {
        applyRoomMemberEvents(this.room, events);
      }
      return;
    }

    // Единственный normal caller использует forceServer. Этот legacy путь
    // делегирован SDK и не позволяет отделить fetch от внутреннего apply.
    const room = this.client.getRoom(roomId);
    await room?.loadMembersIfNeeded();
  }

  protected override getMode(): "standalone" {
    return "standalone";
  }
}

export class WidgetRoomMembersHydrator
  extends BaseRoomMembersHydrator
  implements RoomMembersHydrator, AuthoritativeRoomMembersHydrator
{
  public async hydrateAllRoomMembers(
    roomId: string,
    signal: AbortSignal,
  ): Promise<void> {
    if (roomId !== this.room.roomId) {
      throw new Error("Authoritative hydration requested for foreign room");
    }
    const generation = this.beginAuthoritativeHydration();
    throwIfAborted(signal);
    if (!widget) {
      throw new Error("Widget mode hydration requested without widget API");
    }
    await widget.api.transport.send(
      ElementWidgetActions.ForceHydrateRoomMembers,
      {},
    );
    throwIfAborted(signal);
    const events = await fetchMembersFromWidgetHost(roomId);
    throwIfAborted(signal);
    if (!this.canApplyHydration(generation)) return;
    applyRoomMemberEvents(this.room, events);
  }

  protected override async hydrateRoomMembers(
    roomId: string,
    options: RoomMembersHydrationOptions,
    generation: number,
  ): Promise<void> {
    if (options.forceServer) {
      if (!widget) {
        throw new Error("Widget mode hydration requested without widget API");
      }

      await widget.api.transport.send(
        ElementWidgetActions.ForceHydrateRoomMembers,
        {},
      );
      const events = await fetchMembersFromWidgetHost(roomId);
      if (this.canApplyHydration(generation)) {
        applyRoomMemberEvents(this.room, events);
      }
      return;
    }

    const room = this.client.getRoom(roomId);
    await room?.loadMembersIfNeeded();
  }

  protected override getMode(): "widget" {
    return "widget";
  }
}

export function createRoomMembersHydrator(
  client: MatrixClient,
  room: Room,
): RoomMembersHydrationAdapter {
  if (widget) {
    return new WidgetRoomMembersHydrator(client, room);
  }

  return new StandaloneRoomMembersHydrator(client, room);
}
